import { create } from "zustand";
import { batchBonusApi, type BatchBonusPopupModel } from "@/api/batchBonusApi";
import { Message } from "@/components/Base/Message";
import i18n from "@/i18n";
import utils from "@/utils/utils";

const AUTO_SHOWN_KEY = "bb-auto-shown";

/** 用户 id + token 指纹：重新登录才会再自动弹 */
const loginMark = () => {
  let token = "";
  let userId = "";
  try {
    const u = JSON.parse(utils.getUser() || "null");
    token = u?.token || "";
    userId = u?.userId || u?.id || "";
  } catch {
    /* ignore */
  }
  let h = 0;
  for (let i = 0; i < token.length; i++) h = (h * 31 + token.charCodeAt(i)) | 0;
  return `${userId}:${h}`;
};

const readAutoShown = () => {
  try {
    return localStorage.getItem(AUTO_SHOWN_KEY) === loginMark();
  } catch {
    return false;
  }
};

interface BatchBonusPopupState {
  model: BatchBonusPopupModel | null;
  autoShown: boolean;
  manualOpen: boolean;
  /** 充值达标待再弹：回首页后走弹窗队列 */
  qualifiedPending: boolean;
  load: (recordId?: string) => Promise<BatchBonusPopupModel | null>;
  openManual: (recordId?: string, fromNotice?: boolean) => Promise<boolean>;
  queueQualified: (recordId?: string) => Promise<void>;
  closeManual: () => void;
  markAutoShown: () => void;
  refresh: () => Promise<void>;
  reset: () => void;
}

const fetchModel = async (recordId?: string) => {
  try {
    const sentAt = Date.now();
    const resp = (await batchBonusApi.popup(recordId)) as any;
    const m = (resp?.data?.model ?? null) as BatchBonusPopupModel | null;
    return m ? { ...m, fetchedAt: (sentAt + Date.now()) / 2 } : null;
  } catch {
    return null;
  }
};

export const useBatchBonusPopupStore = create<BatchBonusPopupState>(
  (set, get) => ({
    model: null,
    autoShown: false,
    manualOpen: false,
    qualifiedPending: false,
    load: async recordId => {
      const m = await fetchModel(recordId);
      // 按 id 取不到时保留原数据，浮动图标不受影响
      if (m || !recordId) set({ model: m });
      return m;
    },
    openManual: async (recordId, fromNotice = !!recordId) => {
      const m = await get().load(recordId);
      if (!m) {
        if (recordId) Message.show(String(i18n.t("batchBonus.activityEnded")));
        return false;
      }
      set({ model: { ...m, fromNotice }, manualOpen: true });
      return true;
    },
    queueQualified: async recordId => {
      const m = await get().load(recordId);
      if (m) set({ qualifiedPending: true });
    },
    closeManual: () => set({ manualOpen: false }),
    markAutoShown: () => {
      try {
        localStorage.setItem(AUTO_SHOWN_KEY, loginMark());
      } catch {
        /* ignore */
      }
      set({ autoShown: true, qualifiedPending: false });
    },
    refresh: async () => {
      set({ manualOpen: false });
      await get().load();
    },
    reset: () =>
      set({
        model: null,
        autoShown: readAutoShown(),
        manualOpen: false,
        qualifiedPending: false,
      }),
  })
);
